import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

interface CheckIn {
  id: string;
  driver_name: string;
  driver_card_number: string;
  registration_number: string;
  check_in_time: string;
  inspection_completed: boolean;
  inspection_completed_at?: string;
  compliance_status: 'compliant' | 'pending' | 'overdue';
}

interface ComplianceStats {
  total_check_ins: number;
  completed_inspections: number;
  missing_inspections: number;
  overdue_inspections: number;
}

const Compliance: React.FC = () => {
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [stats, setStats] = useState<ComplianceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [checkInsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/check-ins${filter === 'non-compliant' ? '?status=non-compliant' : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/check-ins/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCheckIns(checkInsRes.data.data);
      setStats(statsRes.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching compliance data:', error);
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'compliant':
        return '✓ Compliant';
      case 'pending':
        return '⏳ Pending';
      case 'overdue':
        return '⚠️ Overdue';
      default:
        return status;
    }
  };

  const complianceRate = stats 
    ? Math.round((stats.completed_inspections / stats.total_check_ins) * 100) 
    : 0;

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Compliance Dashboard</h1>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm text-gray-600 mb-2">Total Check-Ins (7 Days)</p>
            <p className="text-3xl font-bold">{stats.total_check_ins}</p>
          </div>
          <div className="bg-green-50 p-6 rounded-lg shadow">
            <p className="text-sm text-gray-600 mb-2">Completed Inspections</p>
            <p className="text-3xl font-bold text-green-600">{stats.completed_inspections}</p>
          </div>
          <div className="bg-yellow-50 p-6 rounded-lg shadow">
            <p className="text-sm text-gray-600 mb-2">Missing Inspections</p>
            <p className="text-3xl font-bold text-yellow-600">{stats.missing_inspections}</p>
          </div>
          <div className="bg-red-50 p-6 rounded-lg shadow">
            <p className="text-sm text-gray-600 mb-2">Overdue (30+ min)</p>
            <p className="text-3xl font-bold text-red-600">{stats.overdue_inspections}</p>
          </div>
        </div>
      )}

      {/* Compliance Rate */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">Compliance Rate</h2>
          <span className={`text-2xl font-bold ${complianceRate >= 90 ? 'text-green-600' : complianceRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
            {complianceRate}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div 
            className={`h-4 rounded-full ${complianceRate >= 90 ? 'bg-green-600' : complianceRate >= 70 ? 'bg-yellow-600' : 'bg-red-600'}`}
            style={{ width: `${complianceRate}%` }}
          />
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="all">All Check-Ins</option>
          <option value="non-compliant">Non-Compliant Only</option>
        </select>
      </div>

      {/* Check-Ins Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Card Number</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-In Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inspection Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {checkIns.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No check-ins found
                </td>
              </tr>
            ) : (
              checkIns.map((checkIn) => (
                <tr key={checkIn.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{checkIn.driver_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{checkIn.driver_card_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{checkIn.registration_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {new Date(checkIn.check_in_time).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs rounded-full ${getStatusBadge(checkIn.compliance_status)}`}>
                      {getStatusText(checkIn.compliance_status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {checkIn.inspection_completed_at 
                      ? new Date(checkIn.inspection_completed_at).toLocaleString()
                      : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Alert Box for Overdue */}
      {stats && stats.overdue_inspections > 0 && (
        <div className="mt-6 bg-red-50 border-l-4 border-red-600 p-4 rounded">
          <div className="flex items-center">
            <span className="text-2xl mr-3">⚠️</span>
            <div>
              <h3 className="text-red-800 font-semibold">Compliance Alert</h3>
              <p className="text-red-700">
                {stats.overdue_inspections} driver(s) checked in over 30 minutes ago without completing inspections
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Compliance;
